import clsx from 'clsx';
import { isNil, toLower } from 'rambdax';

import {
  ActivityStatus,
  AssetRegistrationStatus,
  CancelReason,
  MarketStatus,
  OrderStatus,
  TransferStatus,
} from '@ping/api';
import HelpIcon from '@ping/assets/Icon/help.svg';
import { Tooltip } from '@ping/uikit';
import { format } from '@ping/utils';

import style from './style.module.scss';

const CANCEL_REASON = {
  [CancelReason.CancelledByAdmin]: 'The order was cancelled due to a violation of the Terms of Service.',
  [CancelReason.CancelledByTrader]: 'This order has been cancelled by the trader.',
  [CancelReason.CancelledBySystem]: 'This order has been cancelled by the system.',
  [CancelReason.SelfMatchPrevention]: 'This order has been cancelled due to it matching your previous counter order.',
  [CancelReason.NoMoreMatchesAvailable]: 'This order has been cancelled due to no more matches available.',
  [CancelReason.NotCancelled]: 'The order was not cancelled.',
};

const ALL_STATUS_INDICATOR = {
  ...OrderStatus,
  ...TransferStatus,
  ...ActivityStatus,
  ...MarketStatus,
  ...AssetRegistrationStatus,
  Invisible: 'Invisible',
  Internal: 'Internal',
  Visible: 'Visible',
};

interface IndicatorProps {
  status: string;
  red?: string | string[];
  yellow?: string | string[];
  green?: string | string[];
  gray?: string | string[];
  blue?: string | string[];
}

interface IProps extends IndicatorProps {
  hasTooltip?: boolean;
  hasIndicatorOnly?: boolean;
  hint?: string;
}

function Indicator({ status, red, yellow, green, gray, blue }: IndicatorProps) {
  return (
    <i
      className={clsx(style['status-indicator'], {
        [style['bg--green']]: isConsideredStatus(status, green),
        [style['bg--red']]: isConsideredStatus(status, red),
        [style['bg--yellow']]: isConsideredStatus(status, yellow),
        [style['bg--gray']]: isConsideredStatus(status, gray),
        [style['bg--blue']]: isConsideredStatus(status, blue),
      })}
    />
  );
}

function StatusIndicatorContent({ ...rest }: IndicatorProps) {
  const statusLabel = ALL_STATUS_INDICATOR[rest.status] ?? rest.status ?? '';
  return (
    <>
      <Indicator {...rest} />
      <div className={style['status-text']}>{format.string.toTitleCase(statusLabel)}</div>
    </>
  );
}

export function StatusIndicator({ hasTooltip = false, hasIndicatorOnly = false, hint, ...rest }: IProps) {
  if (hint && hint !== CancelReason.NotCancelled) {
    return (
      <div className={style['status-tooltip-wrapper']}>
        <StatusIndicatorContent {...rest} />
        <Tooltip
          position='top'
          childrenIcon={
            <span className={style['help-icon-tooltip']}>
              <HelpIcon />
            </span>
          }
        >
          {CANCEL_REASON[hint]}
        </Tooltip>
      </div>
    );
  }

  if (hasIndicatorOnly) {
    return <Indicator {...rest} />;
  }

  if (hasTooltip) {
    return (
      <Tooltip
        position='top'
        childrenIcon={
          <span className={style['status-indicator-wrapper']}>
            <Indicator {...rest} />
          </span>
        }
        contentClassName={style['status-tooltip-wrapper']}
      >
        <StatusIndicatorContent {...rest} />
      </Tooltip>
    );
  }

  return (
    <div className={style['status-tooltip-wrapper']}>
      <StatusIndicatorContent {...rest} />
    </div>
  );
}

function isConsideredStatus(value: string, group: string | string[]): boolean {
  if (typeof group === 'string') {
    return value === group;
  }
  if (!isNil(group)) {
    return group.map(toLower).includes(toLower(value));
  }
  return false;
}
